import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';

const STATUS_OPTIONS = ['RASCUNHO', 'EMITIDA', 'ENVIADA', 'VENCIDA', 'PARCIAL', 'PAGA', 'CANCELADA', 'RENEGOCIADA'];

const EMPTY_FORM = {
  customer_id: '',
  data_vencimento: '',
  competencia_mes: new Date().getMonth() + 1,
  competencia_ano: new Date().getFullYear(),
  valor_original: '',
  descricao: '',
  juros_percentual_mensal: 0,
  multa_percentual: 0,
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) =>
  toNumber(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  const text = String(value || '').trim();
  if (!text) return '-';
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return text;
};

const OVERDUE_IGNORE_STATUS = new Set(['PAGA', 'CANCELADA', 'RENEGOCIADA']);

const toDateOnlyUtc = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3])));
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
};

const isOpenAndOverdue = (item, now = new Date()) => {
  const aberto = Math.max(0, toNumber(item?.saldo_aberto));
  if (aberto <= 0) return false;

  const status = String(item?.status || '').toUpperCase();
  if (OVERDUE_IGNORE_STATUS.has(status)) return false;

  const diasAtraso = toNumber(item?.dias_atraso);
  if (diasAtraso > 0) return true;

  const dueDate = toDateOnlyUtc(item?.data_vencimento);
  if (!dueDate) return false;

  const nowUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return dueDate < nowUtc;
};

const formatPercent = (value) =>
  `${toNumber(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

const statusBadge = (status) => {
  if (status === 'PAGA') return 'badge-success';
  if (status === 'VENCIDA' || status === 'CANCELADA') return 'badge-danger';
  if (status === 'PARCIAL') return 'badge-warning';
  return 'badge-info';
};

const rowTone = (item) => {
  if (item?.status === 'PAGA') return 'bg-emerald-50/60';
  if (item?.status === 'VENCIDA' || isOpenAndOverdue(item)) return 'bg-rose-50/70';
  if (item?.status === 'PARCIAL') return 'bg-amber-50/70';
  return '';
};

const ChargesPage = () => {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState({
    status: '',
    customer_id: '',
    source_module: '',
    competencia_mes: '',
    competencia_ano: '',
    q: '',
  });

  const loadLookups = async () => {
    try {
      const { data } = await axios.get('/api/customers', { params: { pageSize: 200, ativo: true } });
      setCustomers(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar clientes');
    }
  };

  const loadCharges = async () => {
    setLoading(true);
    try {
      const params = { pageSize: 200 };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) params[key] = value;
      });
      const { data } = await axios.get('/api/charges', { params });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar cobrancas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    loadCharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.customer_id, filters.source_module, filters.competencia_mes, filters.competencia_ano, filters.q]);

  const customerMap = useMemo(() => {
    const m = new Map();
    customers.forEach((c) => m.set(c.id, c.nome));
    return m;
  }, [customers]);

  const summary = useMemo(() => {
    const result = {
      total: 0,
      recebido: 0,
      aberto: 0,
      aberto_total: 0,
      vencido: 0,
      quantidade: items.length,
      qtd_vencidas: 0,
    };

    items.forEach((item) => {
      const total = Math.max(0, toNumber(item.valor_total_calculado));
      const recebido = Math.max(0, toNumber(item.valor_pago_total));
      const aberto = Math.max(0, toNumber(item.saldo_aberto));
      const isVencida = isOpenAndOverdue(item);

      result.total += total;
      result.recebido += recebido;
      result.aberto_total += aberto;

      if (isVencida) {
        result.vencido += aberto;
        result.qtd_vencidas += 1;
      } else {
        result.aberto += aberto;
      }
    });

    result.ticket_medio = result.quantidade > 0 ? result.total / result.quantidade : 0;
    result.taxa_recebimento = result.total > 0 ? (result.recebido / result.total) * 100 : 0;

    return result;
  }, [items]);

  const createCharge = async () => {
    try {
      if (!form.customer_id || !form.data_vencimento || !form.valor_original) {
        toast.error('Cliente, vencimento e valor sao obrigatorios');
        return;
      }

      const payload = {
        customer_id: form.customer_id,
        source_module: 'CORE',
        status: 'EMITIDA',
        data_vencimento: form.data_vencimento,
        competencia_mes: Number(form.competencia_mes),
        competencia_ano: Number(form.competencia_ano),
        juros_percentual_mensal: Number(form.juros_percentual_mensal || 0),
        multa_percentual: Number(form.multa_percentual || 0),
        items: [
          {
            descricao: form.descricao || 'Cobranca avulsa',
            quantidade: 1,
            valor_unitario: Number(form.valor_original),
            valor_total: Number(form.valor_original),
          },
        ],
      };

      await axios.post('/api/charges', payload);
      toast.success('Cobranca criada');
      setShowCreate(false);
      setForm(EMPTY_FORM);
      await loadCharges();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar cobranca');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      customer_id: '',
      source_module: '',
      competencia_mes: '',
      competencia_ano: '',
      q: '',
    });
  };

  const setCurrentMonth = () => {
    const now = new Date();
    setFilters((old) => ({
      ...old,
      competencia_mes: String(now.getMonth() + 1),
      competencia_ano: String(now.getFullYear()),
    }));
  };

  const setPreviousMonth = () => {
    const now = new Date();
    now.setMonth(now.getMonth() - 1);
    setFilters((old) => ({
      ...old,
      competencia_mes: String(now.getMonth() + 1),
      competencia_ano: String(now.getFullYear()),
    }));
  };

  const competenciaLabel =
    filters.competencia_mes && filters.competencia_ano
      ? `${String(filters.competencia_mes).padStart(2, '0')}/${filters.competencia_ano}`
      : 'periodo filtrado';

  const cards = [
    {
      label: `Total (${competenciaLabel})`,
      value: formatMoney(summary.total),
      hint: `${summary.quantidade} cobranca(s) no resultado`,
      style: 'border-slate-200 bg-slate-50 text-slate-900',
    },
    {
      label: 'Recebido',
      value: formatMoney(summary.recebido),
      hint: `Taxa de recebimento: ${formatPercent(summary.taxa_recebimento)}`,
      style: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    },
    {
      label: 'Em aberto (a vencer)',
      value: formatMoney(summary.aberto),
      hint: `${formatMoney(summary.aberto_total)} em aberto total`,
      style: 'border-amber-200 bg-amber-50 text-amber-900',
    },
    {
      label: 'Vencido',
      value: formatMoney(summary.vencido),
      hint: `${summary.qtd_vencidas} cobranca(s) vencida(s)`,
      style: 'border-rose-200 bg-rose-50 text-rose-900',
    },
  ];

  return (
    <div className="animate-fade-in space-y-6">
      <section className="rounded-2xl border border-cyan-300 bg-gradient-to-r from-slate-900 via-blue-900 to-cyan-800 p-6 text-white shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Modulo de Cobrancas</h1>
            <p className="mt-1 text-cyan-100">Visao consolidada de recebimento, risco e carteira em aberto</p>
          </div>
          <div className="flex items-center gap-3">
            {loading && <div className="loader h-6 w-6 border-white/30 border-t-white" />}
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Nova Cobranca
            </button>
          </div>
        </div>
      </section>

      <div className="card">
        <div className="card-body p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-12 md:items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select className="form-select text-sm py-1.5" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Todos</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Cliente</label>
              <select className="form-select text-sm py-1.5" value={filters.customer_id} onChange={(e) => setFilters({ ...filters, customer_id: e.target.value })}>
                <option value="">Todos</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Modulo</label>
              <select className="form-select text-sm py-1.5" value={filters.source_module} onChange={(e) => setFilters({ ...filters, source_module: e.target.value })}>
                <option value="">Todos</option>
                <option value="CORE">CORE</option>
                <option value="SOLAR">SOLAR</option>
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Mes</label>
              <input className="form-input text-sm py-1.5" type="number" min="1" max="12" value={filters.competencia_mes} onChange={(e) => setFilters({ ...filters, competencia_mes: e.target.value })} />
            </div>
            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Ano</label>
              <input className="form-input text-sm py-1.5" type="number" value={filters.competencia_ano} onChange={(e) => setFilters({ ...filters, competencia_ano: e.target.value })} />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Busca</label>
              <input className="form-input text-sm py-1.5" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Cliente, observacao ou referencia" />
            </div>
            <div className="md:col-span-12 flex flex-wrap justify-end gap-2 pt-1">
              <button className="btn-secondary text-sm py-1.5 px-3" onClick={setCurrentMonth}>
                Mes atual
              </button>
              <button className="btn-secondary text-sm py-1.5 px-3" onClick={setPreviousMonth}>
                Mes anterior
              </button>
              <button className="btn-secondary text-sm py-1.5 px-3" onClick={clearFilters}>
                Limpar
              </button>
            </div>
          </div>
        </div>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className={`rounded-xl border p-4 shadow-sm ${card.style}`}>
            <p className="text-xs uppercase tracking-wide opacity-80">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold">{card.value}</p>
            <p className="mt-1 text-xs opacity-80">{card.hint}</p>
          </div>
        ))}
      </section>

      <div className="card">
        <div className="card-header">
          Lista de Cobrancas ({items.length}) - Ticket medio {formatMoney(summary.ticket_medio)}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Competencia</th>
                  <th>Modulo</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th>Valor</th>
                  <th>Pago</th>
                  <th>Saldo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className={rowTone(item)}>
                    <td>{item.customer_nome || customerMap.get(item.customer_id) || '-'}</td>
                    <td>
                      {String(item.competencia_mes || '').padStart(2, '0')}/{item.competencia_ano || '-'}
                    </td>
                    <td>{item.source_module}</td>
                    <td>{formatDate(item.data_vencimento)}</td>
                    <td>
                      <span className={statusBadge(item.status)}>{item.status}</span>
                    </td>
                    <td className="font-semibold text-slate-900">{formatMoney(item.valor_total_calculado || 0)}</td>
                    <td className="font-semibold text-emerald-700">{formatMoney(item.valor_pago_total || 0)}</td>
                    <td className={toNumber(item.saldo_aberto) > 0 ? 'font-semibold text-amber-700' : 'font-semibold text-slate-700'}>
                      {formatMoney(item.saldo_aberto || 0)}
                    </td>
                    <td>
                      <Link to={`/cobrancas/${item.id}`} className="text-primary-600 hover:text-primary-900 font-medium">
                        Detalhe
                      </Link>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-gray-500 py-10">
                      Nenhuma cobranca encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Nova Cobranca</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="form-label">Cliente</label>
                <select className="form-select" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Vencimento</label>
                <input className="form-input" type="date" value={form.data_vencimento} onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Valor</label>
                <input className="form-input" type="number" step="0.01" value={form.valor_original} onChange={(e) => setForm({ ...form, valor_original: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Competencia Mes</label>
                <input className="form-input" type="number" min="1" max="12" value={form.competencia_mes} onChange={(e) => setForm({ ...form, competencia_mes: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Competencia Ano</label>
                <input className="form-input" type="number" value={form.competencia_ano} onChange={(e) => setForm({ ...form, competencia_ano: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Juros % mensal</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  value={form.juros_percentual_mensal}
                  onChange={(e) => setForm({ ...form, juros_percentual_mensal: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Multa %</label>
                <input className="form-input" type="number" step="0.01" value={form.multa_percentual} onChange={(e) => setForm({ ...form, multa_percentual: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Descricao</label>
                <input className="form-input" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="Ex.: Mensalidade" />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button className="btn-secondary" onClick={() => setShowCreate(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={createCharge}>
                Criar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChargesPage;
